import {inject, Injectable, signal, Signal, WritableSignal} from "@angular/core";
import {onEach} from '../utils/observable-utils';
import {NavigationService} from "../navigation/navigation.service";
import {ButtonConnotation} from "../buttons/button/button";
import {Subject} from "rxjs";
import {MessageDetails, MessageLevel} from "./message/message";
import {
    DataClientError,
    DisplayableDataServerMessage,
    ErrorService,
    ServerLoggingService
} from "@fedelta-pos/data-client";
import {CheckboxSpec} from "./simple-dialog-page/simple-dialog-page.component";

/**
 * Service to show notifications
 */
@Injectable({providedIn: "root"})
export class NotificationService {
    private errorService: ErrorService = inject(ErrorService);
    private serverLoggingService: ServerLoggingService = inject(ServerLoggingService);
    private navigationService: NavigationService = inject(NavigationService);

    private static readonly FOOTER_MESSAGE_DEFAULT_TIMEOUT_SECONDS: number = 5;

    private readonly _alertDisplayed: WritableSignal<boolean> = signal<boolean>(false);
    public readonly alertDisplayed: Signal<boolean> = this._alertDisplayed.asReadonly();
    public readonly footerMessages: WritableSignal<MessageDetails[]> = signal<MessageDetails[]>([]);

    public footerMessagesChanged$: Subject<void> = new Subject<void>();

    constructor() {
        console.log("--- NotificationService");

        onEach(this.errorService.dataClientErrors, async (error: DataClientError) => {
            const displayableMessage: DisplayableDataServerMessage = await error.displayableMessage;
            if (displayableMessage) {
                console.log("NotificationService: data client error: " + displayableMessage.messageKey);
                this.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, displayableMessage.messageKey, displayableMessage.messageParams));
            }
        });

        onEach(this.errorService.errors, (errorMessage: any) => console.error("ErrorHandlerService: unhandled exception: " + errorMessage));
    }

    public displayFooterMessage(message: MessageDetails, closeOthers: boolean = false, timeoutSeconds: number = NotificationService.FOOTER_MESSAGE_DEFAULT_TIMEOUT_SECONDS) {
        this.footerMessages.set([message, ...(closeOthers ? [] : this.footerMessages())]);
        this.footerMessagesChanged$.next();
        setTimeout(() => this.closeFooterMessage(message), timeoutSeconds * 1000);
    }

    public async displayFooterError(error: unknown, defaultKey: string, defaultParams: Record<string, string | number> = {}): Promise<void> {
        const displayableMessage: DisplayableDataServerMessage = error instanceof DataClientError ? await error.displayableMessage : undefined;
        this.displayFooterMessage(new MessageDetails(MessageLevel.ERROR,
            displayableMessage ? displayableMessage.messageKey : defaultKey,
            displayableMessage ? displayableMessage.messageParams : defaultParams));
    }

    public closeFooterMessage(message: MessageDetails) {
        message.closed = true;
        this.footerMessagesChanged$.next();
        setTimeout(() => {
            this.footerMessages.set(this.footerMessages().filter((m: MessageDetails) => m !== message));
            this.footerMessagesChanged$.next();
        }, 1000);
    }

    public async displayAlert(title: string, message: string, translationParams?: Record<string, string | number>, cancelResult: boolean = false, confirmLabel: string = "woa.button.common.ok"): Promise<any> {
        this._alertDisplayed.set(true);
        const result: any = await this.navigationService.createPage("woa-simple-dialog-page", {
            showHeader: !!title,
            showCloseButton: false,
            title: title,
            contentText: message,
            translationParams: translationParams,
            buttons: [{label: confirmLabel}]
        });
        this._alertDisplayed.set(false);
        return result === undefined ? cancelResult : result;
    }

    public async displayAlertWithCheckbox(title: string, message: string, checkboxLabel: string, translationParams?: Record<string, string | number>, cancelResult: boolean = false): Promise<boolean> {
        this._alertDisplayed.set(true);
        const result: any = await this.navigationService.createPage("woa-simple-dialog-page", {
            showCloseButton: false,
            title: title,
            contentText: message,
            translationParams: translationParams,
            buttons: [{
                label: "woa.button.common.ok",
                result: (checkboxState: { [name: string]: boolean }) => checkboxState["acknowledged"]
            }],
            checkboxes: [{
                label: checkboxLabel,
                name: "acknowledged"
            }]
        });
        this._alertDisplayed.set(false);
        return result === undefined ? cancelResult : result;
    }

    public async displayConfirmation(title: string, subtitle: string, message: string, cancelLabel: string, confirmLabel: string, reverseConnotations: boolean = false, translationParams?: Record<string, string | number>, cancelResult: boolean = false, checkboxes?: CheckboxSpec[], useHistory: boolean = true): Promise<boolean> {
        this._alertDisplayed.set(true);
        const result: any = await this.navigationService.createPage("woa-simple-dialog-page", {
            showCloseButton: false,
            title: title,
            subtitle: subtitle,
            contentText: message,
            popHistoryOnDismissal: useHistory,
            translationParams: translationParams,
            buttons: [{
                label: cancelLabel,
                connotation: reverseConnotations ? ButtonConnotation.AFFIRMATIVE : ButtonConnotation.NEGATIVE,
                result: false
            }, {
                label: confirmLabel,
                connotation: reverseConnotations ? ButtonConnotation.NEGATIVE : ButtonConnotation.AFFIRMATIVE,
                result: true
            }],
            checkboxes
        }, undefined, false, false, useHistory);
        this._alertDisplayed.set(false);
        return result === undefined ? cancelResult : result;
    }

    public logErrorToServer(message: string, context: any, error: unknown): void {
        message += ". Caused by '" + error.constructor.name + "' (see error).";
        if (error instanceof DataClientError) {
            this.serverLoggingService.log(message, context, error);
        } else {
            try {
                this.serverLoggingService.log(message, context, JSON.stringify(error, Object.getOwnPropertyNames(error)));
            } catch (e) {
                this.serverLoggingService.log(message + " json unavailable.", context, error);
            }
        }
    }

    public logInfoToServer(message: string): void {
        this.serverLoggingService.log(message);
    }
}
